import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AuthorBio from './AuthorBio';
import { InArticleAd, DisplayAd, HorizontalAd } from './AdSense';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/posts/${slug}`);
        setPost(response.data);

        const allPostsResponse = await axios.get(`${API_URL}/api/blog/posts`);
        const related = allPostsResponse.data
          .filter(p => p.slug !== slug && p.categories.some(cat => response.data.categories.includes(cat)))
          .slice(0, 3);
        setRelatedPosts(related);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setLoading(false);
      }
    };
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold-500 text-xl">Loading post...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center flex-col gap-4">
        <div className="text-gray-400 text-xl">Post not found</div>
        <Button onClick={() => navigate('/blog')} className="bg-gold-500 hover:bg-gold-600 text-black">
          Back to Blog
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.seo_title || post.title}</title>
        <meta name="description" content={post.seo_description || post.excerpt} />
        {post.tags && post.tags.length > 0 && (
          <meta name="keywords" content={post.tags.join(', ')} />
        )}
        <meta name="author" content={post.author} />
        <link rel="canonical" href={`https://aryanshivhare.com/blog/${post.slug}`} />

        <meta property="og:title" content={post.seo_title || post.title} />
        <meta property="og:description" content={post.seo_description || post.excerpt} />
        <meta property="og:image" content={post.featured_image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://aryanshivhare.com/blog/${post.slug}`} />
        <meta property="article:author" content={post.author} />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:modified_time" content={post.updated_at} />
        {post.tags && post.tags.map((tag) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seo_title || post.title} />
        <meta name="twitter:description" content={post.seo_description || post.excerpt} />
        <meta name="twitter:image" content={post.featured_image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.seo_description || post.excerpt,
            "image": post.featured_image,
            "datePublished": post.created_at,
            "dateModified": post.updated_at,
            "author": {
              "@type": "Person",
              "name": post.author,
              "url": "https://aryanshivhare.com"
            },
            "publisher": {
              "@type": "Person",
              "name": "Aryan Shivhare",
              "logo": {
                "@type": "ImageObject",
                "url": "https://aryanshivhare.com/as-logo.jpg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://aryanshivhare.com/blog/${post.slug}`
            },
            "keywords": (post.tags || []).join(', '),
            "articleSection": (post.categories || []).join(', ')
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-24 pb-16">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <Button 
            onClick={() => navigate('/blog')} 
            variant="ghost"
            className="text-gray-400 hover:text-gold-500 -ml-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            </div>
          </div>
        )}

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-6">
            {post.categories.map((cat) => (
              <Badge key={cat} className="bg-gold-500/20 text-gold-500 hover:bg-gold-500/30">
                {cat}
              </Badge>
            ))}
          </div>

          {/* Title */}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8 pb-8 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center text-black font-bold">
                AS
              </div>
              <span className="font-semibold">{post.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.read_time} min read</span>
            </div>
            <Button 
              onClick={handleShare}
              variant="ghost" 
              size="sm"
              className="ml-auto text-gray-400 hover:text-gold-500"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Excerpt */}
          <div className="text-lg md:text-xl text-gray-300 mb-12 font-medium leading-relaxed">
            {post.excerpt}
          </div>

          {/* Ad - After Excerpt (Top of Article) */}
          <HorizontalAd className="mb-12" />

          {/* Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-display prose-headings:text-gold-500 
              prose-h1:text-4xl prose-h1:mb-6
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-gold-500 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white prose-strong:font-bold
              prose-ul:text-gray-300 prose-ol:text-gray-300 prose-li:mb-2 prose-li:marker:text-gold-500
              prose-blockquote:border-gold-500 prose-blockquote:text-gray-400
              prose-img:rounded-xl prose-img:w-full prose-img:my-10 prose-img:border prose-img:border-gray-800
              prose-code:text-gold-400 prose-code:bg-gray-900 prose-code:px-2 prose-code:py-1 prose-code:rounded
            "
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content.replace(/^#\s+.*(\r?\n)+/, '')}</ReactMarkdown>
          </div>

          {/* Ad - After Content (Before Tags) */}
          <InArticleAd className="mt-12" />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag className="w-5 h-5 text-gold-500" />
                {post.tags.map((tag) => (
                  <span key={tag} className="text-sm text-gray-400 hover:text-gold-500 cursor-pointer">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Author Bio (E-E-A-T) */}
          <AuthorBio />

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-gold-500/10 to-gold-500/5 border border-gold-500/20 rounded-xl p-8 text-center">
            <h3 className="font-display text-2xl font-bold text-gold-500 mb-3">
              Ready to Grow Your Brand?
            </h3>
            <p className="text-gray-300 mb-6">
              Let&apos;s discuss how these strategies can work for your business.
            </p>
            <Button 
              onClick={() => navigate('/#contact')}
              className="bg-gold-500 hover:bg-gold-600 text-black font-semibold"
            >
              Get in Touch
            </Button>
          </div>

          {/* Ad - After CTA */}
          <DisplayAd className="mt-12" />
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
            <h2 className="font-display text-3xl font-bold text-gold-500 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <div 
                  key={relatedPost.id}
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                  className="cursor-pointer group"
                >
                  {relatedPost.featured_image && (
                    <div className="relative h-48 rounded-lg overflow-hidden mb-4">
                      <img 
                        src={relatedPost.featured_image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-white group-hover:text-gold-500 transition-colors mb-2 line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPost;